import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import { User, CreateTeamData, DoublesConfig } from '../services/api';

interface TeamRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (teamData: CreateTeamData) => Promise<void>;
  availablePlayers: User[];
  doublesConfig?: DoublesConfig;
}

const TeamRegistrationDialog: React.FC<TeamRegistrationDialogProps> = ({
  open,
  onClose,
  onSubmit,
  availablePlayers,
  doublesConfig,
}) => {
  const [player1Id, setPlayer1Id] = useState('');
  const [player2Id, setPlayer2Id] = useState('');
  const [doublesType, setDoublesType] = useState<'men' | 'women' | 'mixed'>('mixed');
  const [teamName, setTeamName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setPlayer1Id('');
      setPlayer2Id('');
      setDoublesType('mixed');
      setTeamName('');
      setError('');
    }
  }, [open]);

  // Get available doubles types based on config
  const getAvailableDoublesTypes = () => {
    const types: Array<{ value: 'men' | 'women' | 'mixed'; label: string }> = [];
    
    if (!doublesConfig) {
      return [{ value: 'mixed' as const, label: 'Mixed Doubles' }];
    }

    if (doublesConfig.allow_mens_doubles) {
      types.push({ value: 'men', label: "Men's Doubles" });
    }
    if (doublesConfig.allow_womens_doubles) {
      types.push({ value: 'women', label: "Women's Doubles" });
    }
    if (doublesConfig.allow_mixed_doubles) {
      types.push({ value: 'mixed', label: 'Mixed Doubles' });
    }

    return types;
  };

  // Filter player 2 options based on doubles type and player 1 selection
  const getFilteredPlayer2Options = () => {
    if (!player1Id) return availablePlayers;

    const player1 = availablePlayers.find(p => p.id === player1Id);
    if (!player1) return availablePlayers;

    // Exclude player 1 from player 2 options
    let filtered = availablePlayers.filter(p => p.id !== player1Id);

    // For mixed doubles, filter by opposite gender
    if (doublesType === 'mixed') {
      const oppositeGender = player1.gender === 'male' ? 'female' : 'male';
      filtered = filtered.filter(p => p.gender === oppositeGender);
    } else if (doublesType === 'men') {
      filtered = filtered.filter(p => p.gender === 'male');
    } else if (doublesType === 'women') {
      filtered = filtered.filter(p => p.gender === 'female');
    }

    return filtered;
  };

  const validateTeam = (): string | null => {
    if (!player1Id || !player2Id) {
      return 'Please select both players';
    }

    if (player1Id === player2Id) {
      return 'Please select different players';
    }

    const player1 = availablePlayers.find(p => p.id === player1Id);
    const player2 = availablePlayers.find(p => p.id === player2Id);

    if (!player1 || !player2) {
      return 'Selected players not found';
    }

    // Validate gender composition
    if (doublesType === 'mixed') {
      if (player1.gender === player2.gender) {
        return 'Mixed doubles requires one male and one female player';
      }
    } else if (doublesType === 'men') {
      if (player1.gender !== 'male' || player2.gender !== 'male') {
        return "Men's doubles requires both players to be male";
      }
    } else if (doublesType === 'women') {
      if (player1.gender !== 'female' || player2.gender !== 'female') {
        return "Women's doubles requires both players to be female";
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    setError('');
    
    const validationError = validateTeam();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        player1_id: player1Id,
        player2_id: player2Id,
        doubles_type: doublesType,
        team_name: teamName || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to register team');
    } finally {
      setLoading(false);
    }
  };

  const handleDoublesTypeChange = (event: SelectChangeEvent) => {
    setDoublesType(event.target.value as 'men' | 'women' | 'mixed');
    // Reset player selections when doubles type changes
    setPlayer2Id('');
  };

  const availableTypes = getAvailableDoublesTypes();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Register Doubles Team</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth>
            <InputLabel>Doubles Type</InputLabel>
            <Select
              value={doublesType}
              label="Doubles Type"
              onChange={handleDoublesTypeChange}
            >
              {availableTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Player 1</InputLabel>
            <Select
              value={player1Id}
              label="Player 1"
              onChange={(e) => {
                setPlayer1Id(e.target.value);
                setPlayer2Id(''); // Reset player 2 when player 1 changes
              }}
            >
              {availablePlayers.map(player => (
                <MenuItem key={player.id} value={player.id}>
                  {player.name} ({player.gender})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!player1Id}>
            <InputLabel>Player 2</InputLabel>
            <Select
              value={player2Id}
              label="Player 2"
              onChange={(e) => setPlayer2Id(e.target.value)}
            >
              {getFilteredPlayer2Options().map(player => (
                <MenuItem key={player.id} value={player.id}>
                  {player.name} ({player.gender})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {doublesType === 'mixed' && player1Id && (
            <Typography variant="caption" color="text.secondary">
              Player 2 options are filtered to show only players of the opposite gender
            </Typography>
          )}

          <TextField
            label="Team Name (Optional)"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            fullWidth
            helperText="Leave empty to auto-generate from player names"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !player1Id || !player2Id}
        >
          {loading ? 'Registering...' : 'Register Team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamRegistrationDialog;
